/*
 * Lumao post-order uploader
 * ---------------------------------------------------------------
 * Samostatná stránka „Nahrát fotku k objednávce" (/pages/nahrat-fotku).
 * Zákazník, který si lampičku koupil bez fotky, sem dorazí z odkazu v
 * potvrzovacím e-mailu (…?cislo=<číslo objednávky>) a nahraje fotku.
 *
 * - číslo objednávky se předvyplní z URL parametru ?cislo= (nebo ?order=)
 * - fotka jde přímo z prohlížeče na Cloudinary (stejný unsigned preset jako produkt)
 * - číslo objednávky se zapíše do názvu souboru + context/tags, aby šla fotka
 *   v Cloudinary najít podle objednávky (žádný backend)
 * - bez fotky / souhlasu / čísla objednávky nejde odeslat
 */
if (!customElements.get('lumao-post-order')) {
  customElements.define(
    'lumao-post-order',
    class LumaoPostOrder extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.cloud = this.dataset.cloud;
        this.preset = this.dataset.preset;
        this.maxBytes = (parseFloat(this.dataset.maxMb) || 25) * 1024 * 1024;

        this.formEl = this.querySelector('[data-form]');
        this.successEl = this.querySelector('[data-success]');
        this.orderInput = this.querySelector('[data-order]');
        this.fileInput = this.querySelector('[data-file]');
        this.drop = this.querySelector('[data-drop]');
        this.dropText = this.querySelector('[data-droptext]');
        this.thumb = this.querySelector('[data-thumb]');
        this.status = this.querySelector('[data-status]');
        this.consent = this.querySelector('[data-consent]');
        this.consentWrap = this.querySelector('[data-consent-wrap]');
        this.submit = this.querySelector('[data-submit]');

        this.file = null;
        this.uploading = false;

        this.prefillOrder();
        this.bindEvents();
        this.updateSubmit();
      }

      prefillOrder() {
        if (!this.orderInput) return;
        try {
          const params = new URLSearchParams(window.location.search);
          const raw = params.get('cislo') || params.get('order') || params.get('objednavka') || '';
          if (raw) this.orderInput.value = raw.trim();
        } catch (_) {
          /* ignore */
        }
      }

      bindEvents() {
        if (this.orderInput) this.orderInput.addEventListener('input', () => this.updateSubmit());
        if (this.consent) this.consent.addEventListener('change', () => this.updateSubmit());

        if (this.fileInput) {
          this.fileInput.addEventListener('change', (e) => this.onFile(e.target.files[0]));
        }
        if (this.drop) {
          ['dragover', 'dragenter'].forEach((ev) =>
            this.drop.addEventListener(ev, (e) => {
              e.preventDefault();
              this.drop.classList.add('is-over');
            })
          );
          ['dragleave', 'drop'].forEach((ev) =>
            this.drop.addEventListener(ev, (e) => {
              e.preventDefault();
              this.drop.classList.remove('is-over');
            })
          );
          this.drop.addEventListener('drop', (e) => {
            const f = e.dataTransfer && e.dataTransfer.files[0];
            if (f) this.onFile(f);
          });
          this.drop.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.fileInput.click();
            }
          });
        }

        if (this.submit) this.submit.addEventListener('click', () => this.send());
      }

      onFile(file) {
        if (!file) return;
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
          this.setStatus('Nepodporovaný formát. Použij JPG, PNG nebo WEBP.', 'err');
          return;
        }
        if (file.size > this.maxBytes) {
          this.setStatus('Fotka je moc velká (max 25 MB).', 'err');
          return;
        }
        this.file = file;
        this.classList.add('has-photo');
        if (this.thumb) {
          this.thumb.src = URL.createObjectURL(file);
          this.thumb.hidden = false;
        }
        if (this.dropText) this.dropText.textContent = 'Změnit fotku';
        if (this.consentWrap) this.consentWrap.hidden = false;
        this.setStatus('', '');
        this.updateSubmit();
      }

      updateSubmit() {
        const hasOrder = !!(this.orderInput && this.orderInput.value.trim());
        const hasPhoto = !!this.file;
        const hasConsent = !this.consent || this.consent.checked;
        const ok = hasOrder && hasPhoto && hasConsent && !this.uploading;
        if (this.submit) {
          this.submit.disabled = !ok;
          this.submit.setAttribute('aria-disabled', ok ? 'false' : 'true');
        }
      }

      async send() {
        if (this.uploading || !this.file) return;
        const order = (this.orderInput && this.orderInput.value.trim()) || '';
        if (!order) {
          this.setStatus('Doplň prosím číslo objednávky.', 'err');
          return;
        }
        this.uploading = true;
        this.updateSubmit();
        this.setStatus('Nahrávám fotku…', 'load');

        try {
          const blob = await this.downscale(this.file);
          const safeOrder = order.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 40) || 'objednavka';
          const filename = 'objednavka-' + safeOrder + '.jpg';

          let json = await this.cloudinaryUpload(blob, filename, safeOrder, true);
          if (!json) json = await this.cloudinaryUpload(blob, filename, safeOrder, false); // fallback bez context/tags
          if (!json || !json.secure_url) throw new Error('Chybí secure_url');

          this.showSuccess();
        } catch (err) {
          console.error('Lumao post-order: nahrání selhalo', err);
          this.uploading = false;
          this.setStatus('Nahrání se nepovedlo. Zkus to prosím znovu, nebo nám fotku pošli e-mailem.', 'err');
          this.updateSubmit();
        }
      }

      async cloudinaryUpload(blob, filename, order, withMeta) {
        if (!this.cloud || !this.preset) throw new Error('Úložiště fotek není nastavené.');
        const fd = new FormData();
        fd.append('file', blob, filename);
        fd.append('upload_preset', this.preset);
        if (withMeta) {
          // Pomáhá najít fotku v Cloudinary podle objednávky (pokud to preset dovolí).
          fd.append('tags', order);
          fd.append('context', 'objednavka=' + order);
        }
        const res = await fetch(`https://api.cloudinary.com/v1_1/${this.cloud}/image/upload`, {
          method: 'POST',
          body: fd,
        });
        if (!res.ok) {
          if (withMeta) return null; // zkus znovu bez context/tags
          throw new Error('HTTP ' + res.status);
        }
        return res.json();
      }

      showSuccess() {
        if (this.formEl) this.formEl.hidden = true;
        if (this.successEl) {
          this.successEl.hidden = false;
          this.successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      setStatus(msg, kind) {
        if (!this.status) return;
        this.status.textContent = msg;
        this.status.dataset.kind = kind || '';
      }

      downscale(file) {
        return new Promise((resolve) => {
          const MAX = 2400;
          const url = URL.createObjectURL(file);
          const im = new Image();
          im.onload = () => {
            const longest = Math.max(im.width, im.height);
            if (longest <= MAX && file.size <= 8 * 1024 * 1024) {
              URL.revokeObjectURL(url);
              resolve(file);
              return;
            }
            const scale = Math.min(1, MAX / longest);
            const c = document.createElement('canvas');
            c.width = Math.round(im.width * scale);
            c.height = Math.round(im.height * scale);
            c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
            c.toBlob((b) => {
              URL.revokeObjectURL(url);
              resolve(b || file);
            }, 'image/jpeg', 0.92);
          };
          im.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(file);
          };
          im.src = url;
        });
      }
    }
  );
}
